import fs from 'fs' // ssr
import path from 'path' // ssr

import dynamic from 'next/dynamic'

const Container = dynamic(() => import('../../components').then(mod => mod.Container))
const NextProject = dynamic(() => import('../../components').then(mod => mod.NextProject))
const HeroProject = dynamic(() => import('../../components').then(mod => mod.HeroProject))
const MainContainer = dynamic(() => import('../../components').then(mod => mod.MainContainer))

import { ProjectBuilder } from '../../components'
import { getAllProjects } from '../../services/projects'
import { isObjectEmpty } from '../../utils'
import { getAcfProjectsBySlug } from '../../services'
import getGlobalData from '../../services/cacheGlobalData'

/**
 * ⚡️ BUILDER PAGE ⚡️ CASE
 */
export default function Case({ acfFields, nextProject }) {
  const subtitle =
    acfFields?.project?.location?.[0]?.locationsFields?.location?.cityLocation +
    (acfFields?.project?.location?.[0]?.locationsFields?.location?.stateShortname
      ? `, ${acfFields?.project?.location?.[0]?.locationsFields?.location?.stateShortname}`
      : '')

  const nextProjectLocation =
    nextProject?.SingleProjectFields?.location?.[0]?.locationsFields?.location?.cityLocation +
    (nextProject?.SingleProjectFields?.location?.[0]?.locationsFields?.location?.stateShortname
      ? `, ${nextProject?.SingleProjectFields?.location?.[0]?.locationsFields?.location?.stateShortname}`
      : '')

  const projectSize = acfFields?.project?.headerProjectSize
  const owner = acfFields?.project?.headerOwner
  const regionalOffice = acfFields?.project?.headerRegionalOffice
    ? acfFields?.project?.headerRegionalOffice[0]?.locationsFields?.location
    : null

  const leedCertification = acfFields?.project?.headerLeedCertification

  let details = []
  projectSize && details.push({ detail: projectSize, title: 'Size' })
  acfFields?.project?.headerDetails && details.push(...acfFields?.project?.headerDetails)
  owner && details.push({ detail: owner, title: 'Owner' })
  regionalOffice &&
    details.push({
      detail: `${regionalOffice?.cityLocation}${regionalOffice?.state ? ', ' + regionalOffice?.state : ''}`,
      title: 'Regional Office',
    })
  leedCertification && details.push({ detail: leedCertification, title: 'Leed Certification' })

  const header = {
    title: acfFields?.title,
    subtitle: subtitle,
    details: details,
    image: acfFields?.project?.headerImage,
  }

  const contentBuilder = acfFields?.project?.contentBuilder

  const nextProjectExists = !isObjectEmpty(nextProject)

  return (
    <MainContainer>
      <Container>
        {/* SECTION - Hero */}
        <HeroProject
          title={header?.title}
          subtitle={header?.subtitle}
          details={header?.details}
          image={header?.image}
        />
      </Container>

      {/* SECTION - Content Builder */}
      <ProjectBuilder data={contentBuilder}></ProjectBuilder>

      {/* SECTION - Next Case */}
      {nextProjectExists ? (
        <Container>
          <NextProject
            href={nextProject?.uri}
            title={nextProject?.title}
            location={nextProjectLocation}
          />
        </Container>
      ) : null}
    </MainContainer>
  )
}

/**
 * Find Next project
 * @param {Array} allSelectedProjects All selected projects
 * @return {Object} nextProject
 */
function findNextProject(currentProject, allProjectsPosts) {
  let nextProject = {}

  if (!currentProject) {
    return nextProject
  }

  // Filter by current project type
  const filteredProjects = allProjectsPosts.filter(project => {
    return project.type == currentProject.type
  })

  const totalProjects = filteredProjects.length

  if (totalProjects > 1) {
    const currentProjectIndex = filteredProjects.findIndex(
      project => project?.slug == currentProject?.slug
    )

    const nextProjectIndex = currentProjectIndex == totalProjects - 1 ? 0 : currentProjectIndex + 1
    nextProject = filteredProjects[nextProjectIndex]
  }

  return nextProject
}

const projectsFilePath = path.join(__dirname, 'temp_projects_cache.json')

export async function getStaticPaths() {
  const allProjects = await getAllProjects()

  // Save Projects to temporary file cache
  await fs.writeFileSync(projectsFilePath, JSON.stringify(allProjects), err => {
    if (err) console.log('Projects cache save error:', err)
  })

  const { allProjectsPosts } = allProjects

  const paths =
    allProjectsPosts.map(project => ({
      params: {
        id: project?.id,
        slug: project?.slug,
      },
    })) || []

  return {
    paths,
    fallback: true,
  }
}

export async function getStaticProps({ params, preview = false }) {
  let allProjects = {}

  // load projects from temporary file cache - To generate & show Next Project
  try {
    allProjects = JSON.parse(fs.readFileSync(projectsFilePath, 'utf8'))
  } catch (error) {
    console.log('Projects cache load error:', error)
  }

  // conditionally determine current project
  const currentProject = preview
    ? await getAcfProjectsBySlug(params?.slug)
    : allProjects?.allProjectsPosts?.filter(project => project?.slug === params?.slug)?.[0]

  // enforce projectType on preview mode
  const projectType = preview ? 'Resort' : currentProject?.type

  const acfFields = {
    title: currentProject?.title || '',
    project: currentProject?.SingleProjectFields || {},
    seo: currentProject?.seo || {},
  }

  const additionalHeaderInfo = {
    pageType: 'case',
    text: 'Back to All Projects',
    href: projectType === 'Resort' ? '/resorts' : '/residential',
  }

  // find next project
  const nextProject = findNextProject(currentProject, allProjects?.allProjectsPosts)

  const globalData = await getGlobalData()
  const pageData = { seo: acfFields?.seo || {} }

  return isObjectEmpty(currentProject)
    ? { notFound: true }
    : {
        props: {
          preview,
          globalData,
          pageData,
          acfFields,
          nextProject,
          additionalHeaderInfo,
        },
      }
}
